import { supabase } from '@/integrations/supabase/client';

export interface N8nWorkflow {
  id: string;
  name: string;
  active: boolean;
  nodes: any[];
  connections: any;
  settings?: any;
  tags?: string[];
}

export interface N8nExecution {
  id: string;
  finished: boolean;
  mode: string;
  startedAt: string;
  stoppedAt?: string;
  workflowId: string;
  workflowData?: any;
  data?: any;
}

export interface N8nWebhook {
  httpMethod: string;
  path: string;
  webhookId: string;
  workflowId: string;
}

class N8nService {
  private baseUrl: string;
  private apiKey: string | undefined;

  constructor() {
    // These will be loaded from environment variables
    this.baseUrl = import.meta.env.VITE_N8N_API_URL || 'http://localhost:5678/api/v1';
    this.apiKey = import.meta.env.VITE_N8N_API_KEY;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.apiKey) {
      headers['X-N8N-API-KEY'] = this.apiKey;
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      throw new Error(`n8n API error: ${response.statusText}`);
    }

    return response.json();
  }

  // Workflow Management
  async getWorkflows(): Promise<N8nWorkflow[]> {
    return this.request<N8nWorkflow[]>('/workflows');
  }

  async getWorkflow(id: string): Promise<N8nWorkflow> {
    return this.request<N8nWorkflow>(`/workflows/${id}`);
  }

  async createWorkflow(workflow: Partial<N8nWorkflow>): Promise<N8nWorkflow> {
    return this.request<N8nWorkflow>('/workflows', {
      method: 'POST',
      body: JSON.stringify(workflow),
    });
  }

  async updateWorkflow(
    id: string,
    workflow: Partial<N8nWorkflow>
  ): Promise<N8nWorkflow> {
    return this.request<N8nWorkflow>(`/workflows/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(workflow),
    });
  }

  async deleteWorkflow(id: string): Promise<void> {
    await this.request(`/workflows/${id}`, {
      method: 'DELETE',
    });
  }

  async activateWorkflow(id: string): Promise<N8nWorkflow> {
    return this.updateWorkflow(id, { active: true });
  }

  async deactivateWorkflow(id: string): Promise<N8nWorkflow> {
    return this.updateWorkflow(id, { active: false });
  }

  // Workflow Execution
  async executeWorkflow(
    id: string,
    data?: any
  ): Promise<N8nExecution> {
    return this.request<N8nExecution>(`/workflows/${id}/execute`, {
      method: 'POST',
      body: JSON.stringify({ data }),
    });
  }

  async getExecutions(
    workflowId?: string,
    limit: number = 20
  ): Promise<N8nExecution[]> {
    const params = new URLSearchParams();
    if (workflowId) params.append('workflowId', workflowId);
    params.append('limit', limit.toString());

    return this.request<N8nExecution[]>(`/executions?${params}`);
  }

  async getExecution(id: string): Promise<N8nExecution> {
    return this.request<N8nExecution>(`/executions/${id}`);
  }

  // Webhook Management
  async createWebhook(
    workflowId: string,
    path: string,
    httpMethod: string = 'POST'
  ): Promise<N8nWebhook> {
    // This would typically be done through workflow node configuration
    // n8n doesn't have a direct webhook API, webhooks are created as part of workflows
    const workflow = await this.getWorkflow(workflowId);
    
    // Add webhook node to workflow
    const webhookNode = {
      id: `webhook_${Date.now()}`,
      name: 'Webhook',
      type: 'n8n-nodes-base.webhook',
      position: [250, 300],
      webhookId: crypto.randomUUID(),
      parameters: {
        path,
        httpMethod,
        responseMode: 'onReceived',
        responseData: 'firstEntryJson',
      },
    };

    workflow.nodes = [...(workflow.nodes || []), webhookNode];
    
    await this.updateWorkflow(workflowId, workflow);
    
    return {
      httpMethod,
      path,
      webhookId: webhookNode.webhookId,
      workflowId,
    };
  }

  // CRM-specific workflow templates
  async createCRMWorkflow(type: string, config: any): Promise<N8nWorkflow> {
    const templates = {
      'contact-created': this.getContactCreatedTemplate(config),
      'opportunity-updated': this.getOpportunityUpdatedTemplate(config),
      'task-reminder': this.getTaskReminderTemplate(config),
      'email-campaign': this.getEmailCampaignTemplate(config),
      'data-sync': this.getDataSyncTemplate(config),
    };

    const template = templates[type as keyof typeof templates];
    if (!template) {
      throw new Error(`Unknown workflow type: ${type}`);
    }

    return this.createWorkflow(template);
  }

  private getContactCreatedTemplate(config: any): Partial<N8nWorkflow> {
    return {
      name: 'Contact Created Workflow',
      nodes: [
        {
          id: 'webhook_1',
          name: 'Contact Created Webhook',
          type: 'n8n-nodes-base.webhook',
          position: [250, 300],
          parameters: {
            path: 'contact-created',
            httpMethod: 'POST',
          },
        },
        {
          id: 'email_1',
          name: 'Send Welcome Email',
          type: 'n8n-nodes-base.gmail',
          position: [450, 300],
          parameters: {
            operation: 'send',
            to: '={{$json["email"]}}',
            subject: 'Welcome to Spherical Nexus CRM',
            messageType: 'html',
            message: config.emailTemplate || 'Welcome {{$json["name"]}}!',
          },
        },
        {
          id: 'slack_1',
          name: 'Notify Team',
          type: 'n8n-nodes-base.slack',
          position: [650, 300],
          parameters: {
            operation: 'postMessage',
            channel: config.slackChannel || '#sales',
            text: 'New contact created: {{$json["name"]}} from {{$json["organization"]}}',
          },
        },
      ],
      connections: {
        'webhook_1': {
          main: [[{ node: 'email_1', type: 'main', index: 0 }]],
        },
        'email_1': {
          main: [[{ node: 'slack_1', type: 'main', index: 0 }]],
        },
      },
      settings: {
        executionOrder: 'v1',
      },
    };
  }

  private getOpportunityUpdatedTemplate(config: any): Partial<N8nWorkflow> {
    return {
      name: 'Opportunity Updated Workflow',
      nodes: [
        {
          id: 'webhook_1',
          name: 'Opportunity Updated Webhook',
          type: 'n8n-nodes-base.webhook',
          position: [250, 300],
          parameters: {
            path: 'opportunity-updated',
            httpMethod: 'POST',
          },
        },
        {
          id: 'if_1',
          name: 'Check Stage',
          type: 'n8n-nodes-base.if',
          position: [450, 300],
          parameters: {
            conditions: {
              string: [
                {
                  value1: '={{$json["stage"]}}',
                  operation: 'equals',
                  value2: 'won',
                },
              ],
            },
          },
        },
        {
          id: 'hubspot_1',
          name: 'Update HubSpot',
          type: 'n8n-nodes-base.hubspot',
          position: [650, 200],
          parameters: {
            operation: 'update',
            resource: 'deal',
            dealId: '={{$json["hubspot_id"]}}',
            updateFields: {
              dealstage: 'closedwon',
              amount: '={{$json["value"]}}',
            },
          },
        },
      ],
      connections: {
        'webhook_1': {
          main: [[{ node: 'if_1', type: 'main', index: 0 }]],
        },
        'if_1': {
          main: [
            [{ node: 'hubspot_1', type: 'main', index: 0 }], // true
            [], // false
          ],
        },
      },
    };
  }

  private getTaskReminderTemplate(config: any): Partial<N8nWorkflow> {
    return {
      name: 'Task Reminder Workflow',
      nodes: [
        {
          id: 'cron_1',
          name: 'Daily Check',
          type: 'n8n-nodes-base.cron',
          position: [250, 300],
          parameters: {
            triggerTimes: {
              item: [
                {
                  mode: 'everyDay',
                  hour: 9,
                  minute: 0,
                },
              ],
            },
          },
        },
        {
          id: 'postgres_1',
          name: 'Get Due Tasks',
          type: 'n8n-nodes-base.postgres',
          position: [450, 300],
          parameters: {
            operation: 'executeQuery',
            query: `
              SELECT t.*, u.email, u.full_name
              FROM tasks t
              JOIN user_profiles u ON t.assigned_to = u.id
              WHERE t.due_date = CURRENT_DATE
              AND t.status != 'completed'
            `,
          },
        },
        {
          id: 'email_1',
          name: 'Send Reminders',
          type: 'n8n-nodes-base.gmail',
          position: [650, 300],
          parameters: {
            operation: 'send',
            to: '={{$json["email"]}}',
            subject: 'Task Reminder: {{$json["title"]}}',
            message: 'You have a task due today: {{$json["title"]}}',
          },
        },
      ],
      connections: {
        'cron_1': {
          main: [[{ node: 'postgres_1', type: 'main', index: 0 }]],
        },
        'postgres_1': {
          main: [[{ node: 'email_1', type: 'main', index: 0 }]],
        },
      },
    };
  }

  private getEmailCampaignTemplate(config: any): Partial<N8nWorkflow> {
    return {
      name: 'Email Campaign Workflow',
      nodes: [
        {
          id: 'webhook_1',
          name: 'Campaign Trigger',
          type: 'n8n-nodes-base.webhook',
          position: [250, 300],
          parameters: {
            path: 'campaign-send',
            httpMethod: 'POST',
          },
        },
        {
          id: 'postgres_1',
          name: 'Get Recipients',
          type: 'n8n-nodes-base.postgres',
          position: [450, 300],
          parameters: {
            operation: 'executeQuery',
            query: '={{$json["recipientQuery"]}}',
          },
        },
        {
          id: 'loop_1',
          name: 'Process Recipients',
          type: 'n8n-nodes-base.splitInBatches',
          position: [650, 300],
          parameters: {
            batchSize: 10,
          },
        },
        {
          id: 'email_1',
          name: 'Send Campaign Email',
          type: 'n8n-nodes-base.gmail',
          position: [850, 300],
          parameters: {
            operation: 'send',
            to: '={{$json["email"]}}',
            subject: '={{$node["webhook_1"].json["subject"]}}',
            messageType: 'html',
            message: '={{$node["webhook_1"].json["content"]}}',
          },
        },
      ],
      connections: {
        'webhook_1': {
          main: [[{ node: 'postgres_1', type: 'main', index: 0 }]],
        },
        'postgres_1': {
          main: [[{ node: 'loop_1', type: 'main', index: 0 }]],
        },
        'loop_1': {
          main: [[{ node: 'email_1', type: 'main', index: 0 }]],
        },
      },
    };
  }

  private getDataSyncTemplate(config: any): Partial<N8nWorkflow> {
    return {
      name: 'Data Sync Workflow',
      nodes: [
        {
          id: 'cron_1',
          name: 'Sync Schedule',
          type: 'n8n-nodes-base.cron',
          position: [250, 300],
          parameters: {
            triggerTimes: {
              item: [
                {
                  mode: 'everyHour',
                },
              ],
            },
          },
        },
        {
          id: 'hubspot_1',
          name: 'Get HubSpot Data',
          type: 'n8n-nodes-base.hubspot',
          position: [450, 300],
          parameters: {
            operation: 'getAll',
            resource: config.resource || 'contact',
            returnAll: false,
            limit: 100,
          },
        },
        {
          id: 'postgres_1',
          name: 'Upsert to Database',
          type: 'n8n-nodes-base.postgres',
          position: [650, 300],
          parameters: {
            operation: 'upsert',
            table: config.table || 'contacts',
            columns: config.columns || 'email,first_name,last_name,company',
            updateKey: 'email',
          },
        },
      ],
      connections: {
        'cron_1': {
          main: [[{ node: 'hubspot_1', type: 'main', index: 0 }]],
        },
        'hubspot_1': {
          main: [[{ node: 'postgres_1', type: 'main', index: 0 }]],
        },
      },
    };
  }

  // Trigger workflow from CRM events
  async triggerWorkflow(
    workflowId: string,
    eventType: string,
    data: any
  ): Promise<void> {
    try {
      // Log the event
      await supabase.from('workflow_executions').insert({
        workflow_id: workflowId,
        event_type: eventType,
        payload: data,
        status: 'pending',
      });

      // Execute the workflow
      const execution = await this.executeWorkflow(workflowId, {
        eventType,
        ...data,
      });

      // Update execution status
      await supabase
        .from('workflow_executions')
        .update({
          status: 'completed',
          execution_id: execution.id,
          completed_at: new Date().toISOString(),
        })
        .eq('workflow_id', workflowId)
        .eq('event_type', eventType);
    } catch (error) {
      // Log error
      await supabase
        .from('workflow_executions')
        .update({
          status: 'failed',
          error: error instanceof Error ? error.message : 'Unknown error',
        })
        .eq('workflow_id', workflowId)
        .eq('event_type', eventType);

      throw error;
    }
  }
}

export const n8nService = new N8nService();